import { docsService, httpClient } from '@web/services/singletons';
import { Account } from '@packages/models/api';
import { getLogger } from '@packages/core/logging';

const logger = getLogger('documents');

// Self-contained loading page shown in the new tab while the document URL resolves.
// Must not reference external assets — the tab is still on about:blank.
const LOADING_PAGE_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Opening document…</title>
<style>
    html, body {
        margin: 0;
        height: 100%;
        background: #F4F6F9;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    }
    .wrap {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .card {
        background: #FFFFFF;
        border-radius: 12px;
        box-shadow: 0 8px 30px rgba(13, 19, 33, 0.08);
        padding: 48px 64px;
        text-align: center;
    }
    .spinner {
        width: 44px;
        height: 44px;
        margin: 0 auto 24px;
        border: 4px solid #E3ECF7;
        border-top-color: #1B489B;
        border-radius: 50%;
        animation: spin 0.9s linear infinite;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    h1 {
        margin: 0 0 8px;
        font-size: 18px;
        font-weight: 600;
        color: #0D1321;
    }
    p {
        margin: 0;
        font-size: 14px;
        color: #4C525D;
    }
    .dots::after {
        content: '';
        animation: dots 1.4s steps(4, end) infinite;
    }
    @keyframes dots {
        0% { content: ''; }
        25% { content: '.'; }
        50% { content: '..'; }
        75% { content: '...'; }
    }
</style>
</head>
<body>
<div class="wrap">
    <div class="card">
        <div class="spinner"></div>
        <h1>Opening your document<span class="dots"></span></h1>
        <p>Setting up secure access. This will only take a moment.</p>
    </div>
</div>
</body>
</html>`;

/**
 * Opens a tab synchronously (inside the user's click, while the browser still has
 * transient user activation) and navigates it once the URL resolves. Calling
 * window.open after an await/.then loses the activation and gets blocked as a pop-up.
 *
 * @param resolveUrl async function returning the destination URL
 * @param onBlocked called when the browser blocked even the synchronous open (pop-ups hard-blocked for the site)
 * @returns true when the tab was opened and navigated
 */
export const openDocumentTab = async (
    resolveUrl: () => Promise<string | null | undefined>,
    onBlocked?: () => void
): Promise<boolean> => {
    const tab = window.open('', '_blank');
    if (!tab) {
        logger.warn('Pop-up blocker prevented opening a document tab');
        onBlocked?.();
        return false;
    }
    try {
        tab.document.write(LOADING_PAGE_HTML);
        tab.document.close();
    } catch {
        // placeholder is best-effort — tab may already be navigating
    }
    try {
        const url = await resolveUrl();
        if (url && url.length > 0) {
            tab.location.href = url;
            return true;
        }
        logger.warn('No document URL was resolved, closing the document tab');
        tab.close();
        return false;
    } catch (error) {
        logger.error('Failed to resolve the document URL', { error });
        tab.close();
        return false;
    }
};

// STIS document bundles (Service Diagnostics htmlDiagnostics, and the modelYear/carline
// bundles used by the VIN-session Service Diagnostics button) have index.html as their entry
// point, but for the direct (non-redirect) open the backend intentionally returns just the CDN
// folder (e.g. …/doc/htmlDiagnostics/<folder> or …/doc/2024/Impreza/B23-Impreza). CloudFront
// can't serve a bare folder, so the browser bounces to /redirect/stis, resolves to the same
// folder, and loops forever. Append the index.html entry when the URL points at a folder (the
// last path segment has no file extension). No-op when it already points at a file (PDFs, deep
// .html links), so the redirect paths and any future backend change stay correct.
export const withCdnEntryFile = (url: string | null | undefined): string | null | undefined => {
    if (!url) {
        return url;
    }
    const [pathPart, query] = url.split('?');
    const lastSegment = pathPart.substring(pathPart.lastIndexOf('/') + 1);
    if (lastSegment.includes('.')) {
        return url;
    }
    const withEntry = `${pathPart.replace(/\/$/, '')}/index.html`;
    return query ? `${withEntry}?${query}` : withEntry;
};

// Resolves the bookmarkable CDN URL for a document by id (PDFs and Service Diagnostics
// alike — the old /docs/cdn-cookie-url endpoint was retired). The fetch response also sets
// the CloudFront signed cookies (credentials: 'include'), so it must complete before the tab
// navigates. The returned URL lives on the static cdn.web.<env>.subarutechshare.com domain
// and can be bookmarked; once the cookie expires CloudFront redirects the bookmark back
// through CdnRedirectHandler.
export const fetchCdnDocumentUrlByDocId = async (
    docId: string,
    token: string,
    activityType?: string
): Promise<string | null> => {
    // activityType (e.g. 'share') is forwarded so the backend can distinguish view vs
    // share actions in UserActivityAspect, matching the old /view-doc behavior.
    const activityParam = activityType ? `&activityType=${activityType}` : '';
    const res = await fetch(`${httpClient.vinUrl}/docs/cdn-url-by-docid?docId=${docId}${activityParam}`, {
        headers: { 'X-Subcomm-Access-Token': token },
        credentials: 'include',
    });
    if (!res.ok) {
        logger.error(`Failed to get the CDN URL for document [${docId}]`, { docId, statusCode: res.status });
    }
    const data = await res.json();
    return withCdnEntryFile(data?.documentUrl ?? null) ?? null;
};

// Resolves the bookmarkable CDN URL for a campaign publication by campaign code. The backend
// (view-campaign-doc-cdn) resolves the campaign code to its document and returns the CDN
// documentUrl while setting the CloudFront signed cookies (credentials: 'include'), so the
// fetch must complete before the tab navigates — same contract as cdn-url-by-docid. This is a
// separate endpoint from the mobile /view-campaign-doc (which stays on S3, since mobile
// downloads the PDF locally and can't use the signed-cookie flow). Campaigns are
// DMS/authenticated-only, so there is no public S3 fallback here.
export const fetchCdnCampaignDocUrl = async (campaignCode: string, token: string): Promise<string | null> => {
    const res = await fetch(`${httpClient.vinUrl}/northstar/view-campaign-doc-cdn/${campaignCode}`, {
        headers: { 'X-Subcomm-Access-Token': token },
        credentials: 'include',
    });
    if (!res.ok) {
        logger.error(`Failed to get the CDN URL for campaign [${campaignCode}]`, {
            campaignCode,
            statusCode: res.status,
        });
    }
    const data = await res.json();
    return withCdnEntryFile(data?.documentUrl ?? null) ?? null;
};

// Single source of truth for resolving the URL to open a STIS document, so every
// entry point (table, dashboard cards, preview modal, associate modal, …) behaves
// consistently:
//   - authenticated users → bookmarkable CDN via /docs/cdn-url-by-docid (PDFs AND
//     Service Diagnostics — the old /docs/cdn-cookie-url endpoint was retired)
//   - public / FREE users → S3 pre-signed via /public/view-doc (intentionally not bookmarkable)
// Pair with openDocumentTab so the returned URL opens in the pop-up-safe tab.
export const resolveDocumentUrl = async (
    doc: { documentId: string; docType?: string },
    account: Account | null,
    token: string,
    activityType: string = 'view'
): Promise<string | null | undefined> => {
    if (account) {
        return fetchCdnDocumentUrlByDocId(doc.documentId, token, activityType);
    }
    const response = await docsService.getPublicDocumentUrl({
        queryParameters: { docId: doc.documentId, activityType },
        ignoreCache: true,
    });
    return response.success ? response.data : '';
};
